import React, { memo, useState } from 'react'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { ModalBase, IModalBaseProps, MODAL_TYPES } from 'transfix-ui/components/Modals'
import { RadioGroup } from 'transfix-ui/components/RadioGroup'
import { Box } from 'transfix-ui/core'
import { IError } from 'types/graphqlTypes'
import { ICarrierResultFragment } from '../../../../CarrierAutosuggest/graphql/CarrierResultFragment'
import {
  ICreateCarrierMutationVariables,
  useCreateCarrierMutation,
} from '../graphql/CreateCarrierMutation'
import { ICarrierNumberType } from '../types'

interface ICreateCarrierConfirmationModalProps extends Omit<IModalBaseProps, 'modalType'> {
  carrierNumber: string
  onSuccess: (newCarrier: ICarrierResultFragment) => void
}

const CreateCarrierConfirmationModal = memo<ICreateCarrierConfirmationModalProps>(
  ({ carrierNumber, onClose, onSuccess, open = false, ...props }) => {
    const [formErrors, setFormErrors] = useState<IError[]>([])
    const [carrierNumberType, setCarrierNumberType] = useState(ICarrierNumberType.MC)

    const [createCarrier] = useCreateCarrierMutation()

    const handleConfirm = async () => {
      let createCarrierVariables: ICreateCarrierMutationVariables = {
        input: {
          name: `Auto-Created Carrier ${carrierNumberType}:${carrierNumber}`,
        },
      }

      if (carrierNumberType === ICarrierNumberType.MC) {
        createCarrierVariables = {
          input: {
            ...createCarrierVariables.input,
            mcNumber: carrierNumber,
          },
        }
      } else {
        createCarrierVariables = {
          input: {
            ...createCarrierVariables.input,
            dotNumber: carrierNumber,
          },
        }
      }

      const { data } = await createCarrier({
        variables: createCarrierVariables,
      })

      const { createCarrierApplication } = data || {}
      const { carrierApplication, errors } = createCarrierApplication || {}

      if (errors && errors.length > 0) {
        setFormErrors(errors)
      } else if (carrierApplication) {
        onSuccess(carrierApplication)
      }
    }

    const actions: IModalBaseProps['actions'] = [
      {
        label: 'Cancel',
        action: () => {
          onClose()
        },
        type: 'dismiss',
      },
      {
        label: 'Create Carrier',
        dataTest: 'sd-create-carrier-confirmation-modal-submit',
        action: handleConfirm,
        type: 'confirm',
      },
    ]
    return (
      <ModalBase
        modalType={MODAL_TYPES.confirmation}
        size={5}
        open={open}
        onClose={onClose}
        actions={actions}
        dataTest='sd-create-carrier-confirmation-modal'
        title='Create Carrier with New MC/DOT'
        {...props}>
        <Box minHeight={55}>
          {formErrors.map(({ key, message }) => (
            <InlineMessage key={key} message={message} type={INLINE_MESSAGE_VARIANTS.ALERT} />
          ))}
          <RadioGroup
            label=''
            required={false}
            name='carrierNumberType'
            dataTest='sd-create-carrier-type'
            checked={carrierNumberType}
            onOptionChange={e => {
              setCarrierNumberType(e.target.value)
            }}
            options={[
              {
                value: ICarrierNumberType.MC,
                label: `Create new carrier with MC ${carrierNumber}`,
              },
              {
                value: ICarrierNumberType.DOT,
                label: `Create new carrier with DOT ${carrierNumber}`,
              },
            ]}
            stacked
          />
        </Box>
      </ModalBase>
    )
  }
)

export default CreateCarrierConfirmationModal
